/** 予報（docs/04_world-and-weather.md 8 章）。全員に同じものを見せる。実際の天気にずれを加えて作る */
import type { DayProfile } from '../../config/weatherProfiles';
import { wrapPi } from '../../core/math';
import type { Rng } from '../../core/rng';
import type { LaunchLevel } from '../../core/world';
import type { Vec2 } from '../../core/vec';
import { baseWindAt, cloudBaseAt, type DayWeatherTruth } from './truth';
import { altitudeWindFactor } from './wind';

export interface WindForecast {
  fromDeg: number;
  kmh: number;
}

export interface Forecast {
  dayIndex: number;
  profileName: string;
  windBlocks: { fromH: number; toH: number; valley: WindForecast; ridge: WindForecast; peak: WindForecast }[];
  thermal: { label: '弱' | '並' | '強'; maxClimbMs: number; startH: number; peakH: number; endH: number };
  cloudBase: { h: number; m: number }[];
  storm: {
    chance: number;
    fromH: number;
    toH: number;
    area: { x: number; z: number; radiusM: number; label: string } | null;
  };
  hint: string;
}

const BLOCKS: [number, number][] = [
  [7, 10],
  [10, 13],
  [13, 16],
  [16, 19],
  [19, 21],
];

export function regionLabel(x: number, z: number): string {
  const ns =
    z < 6000
      ? '北の外側の谷'
      : z < 15000
        ? '北の山並み'
        : z < 25000
          ? '主谷'
          : z < 34000
            ? '南の山並み'
            : '南の外側の谷';
  const ew = x < 35000 ? '西部' : x < 70000 ? '中部' : '東部';
  return `${ns}・${ew}`;
}

export function makeForecast(
  truth: DayWeatherTruth,
  profile: DayProfile,
  dayIndex: number,
  rng: Rng,
  stormSites: Vec2[],
): Forecast {
  // 1 日を通したずれ（予報のくせ）と、時間帯ごとの小さなずれ
  const dirBias = rng.range(-1, 1) * ((15 * Math.PI) / 180);
  const spdBias = rng.range(-4, 4);
  const windBlocks = BLOCKS.map(([fromH, toH]) => {
    const mid = ((fromH + toH) / 2) * 3600;
    const w = baseWindAt(truth, mid);
    const kmh2000 = Math.hypot(w.x, w.z) * 3.6;
    const from = Math.atan2(-w.x, w.z) + dirBias + rng.range(-1, 1) * ((6 * Math.PI) / 180);
    const fromDeg = ((((wrapPi(from) * 180) / Math.PI) % 360) + 360) % 360;
    const at = (alt: number): WindForecast => ({
      fromDeg: Math.round(fromDeg / 5) * 5,
      kmh: Math.max(0, Math.round(kmh2000 * altitudeWindFactor(alt) + spdBias + rng.range(-1.5, 1.5))),
    });
    return { fromH, toH, valley: at(800), ridge: at(2500), peak: at(3500) };
  });

  const maxClimb = Math.max(1, truth.thermal.maxClimbMs + rng.range(-0.7, 0.7));
  const thermal = {
    label: (maxClimb < 2.8 ? '弱' : maxClimb < 3.6 ? '並' : '強') as '弱' | '並' | '強',
    maxClimbMs: Math.round(maxClimb * 10) / 10,
    startH: truth.thermal.startH + rng.range(-0.75, 0.75),
    peakH: truth.thermal.peakH + rng.range(-0.5, 0.5),
    endH: truth.thermal.endH + rng.range(-0.5, 0.5),
  };

  const cbBias = rng.range(-300, 300);
  const cloudBase: Forecast['cloudBase'] = [];
  for (let h = 10; h <= 18; h += 2) {
    cloudBase.push({ h, m: Math.round((cloudBaseAt(truth, h * 3600) + cbBias) / 50) * 50 });
  }

  let area: Forecast['storm']['area'] = null;
  const occurring = truth.storms[0];
  const onsetGuess = occurring
    ? occurring.onsetH
    : (profile.storm.onsetHour[0] + profile.storm.onsetHour[1]) / 2 + rng.range(-0.5, 0.5);
  if (profile.storm.chance > 0 && stormSites.length > 0) {
    const base = occurring ? occurring.center : rng.pick(stormSites);
    const ang = rng.range(0, Math.PI * 2);
    const off = occurring ? rng.range(0, 5000) : rng.range(0, 3000);
    const x = base.x + Math.cos(ang) * off;
    const z = base.z + Math.sin(ang) * off;
    area = { x, z, radiusM: 9000, label: regionLabel(x, z) };
  }
  const shift = rng.range(-1, 1);

  return {
    dayIndex,
    profileName: profile.name,
    windBlocks,
    thermal,
    cloudBase,
    storm: {
      chance: profile.storm.chance,
      fromH: onsetGuess - 1 + shift * 0.5,
      toH: onsetGuess + 1 + shift * 0.5,
      area,
    },
    hint: profile.hint,
  };
}

/** 予報の、その時刻・その高さの風 */
export function windForecastAt(f: Forecast, hour: number, level: LaunchLevel): WindForecast {
  const block =
    f.windBlocks.find((b) => hour >= b.fromH && hour < b.toH) ?? f.windBlocks[f.windBlocks.length - 1];
  if (!block) return { fromDeg: 0, kmh: 0 };
  return level === 'peak' ? block.peak : level === 'ridge' ? block.ridge : block.valley;
}
